import { useDeferredValue, useEffect, useMemo, useState } from "react"

import { FleetB2BPageShell } from "@/features/operator/fleet/fleet-b2b-page-shell"
import {
  formatFleetMasterTimestamp,
  useFleetMasterDataWorkspaceContext,
} from "@/features/operator/fleet/fleet-master-data-shared"
import type { FleetOnboardingSearch } from "@/features/operator/fleet/fleet-onboarding-shared"
import {
  OperatorEmptyState,
  OperatorMetricCard,
  OperatorPanel,
  OperatorStatusMessage,
} from "@/features/operator/shared/operator-shell"
import {
  apiClient,
  type FleetOperatingUnitRecord,
  isApiError,
} from "@/lib/api/client"

interface FleetOperatingUnitsPageProps {
  search?: FleetOnboardingSearch
}

interface FleetOperatingUnitFormState {
  city: string
  code: string
  escalationPhone: string
  isPrimary: boolean
  managerName: string
  managerPhone: string
  name: string
  region: string
  type: string
}

const DEFAULT_FORM_STATE: FleetOperatingUnitFormState = {
  city: "",
  code: "",
  escalationPhone: "",
  isPrimary: false,
  managerName: "",
  managerPhone: "",
  name: "",
  region: "",
  type: "branch",
}

function toFormState(record?: FleetOperatingUnitRecord | null): FleetOperatingUnitFormState {
  if (!record) {
    return DEFAULT_FORM_STATE
  }

  return {
    city: record.city,
    code: record.code,
    escalationPhone: record.escalationPhone || "",
    isPrimary: record.isPrimary,
    managerName: record.managerName || "",
    managerPhone: record.managerPhone || "",
    name: record.name,
    region: record.region || "",
    type: record.type,
  }
}

export function FleetOperatingUnitsPage({ search }: FleetOperatingUnitsPageProps) {
  const { organizationId, organizationLabel, sessionError, sessionStatus, vertical } =
    useFleetMasterDataWorkspaceContext(search)
  const [editingUnitId, setEditingUnitId] = useState<string | null>(null)
  const [formState, setFormState] = useState<FleetOperatingUnitFormState>(DEFAULT_FORM_STATE)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeletingUnitId, setIsDeletingUnitId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [searchText, setSearchText] = useState("")
  const [units, setUnits] = useState<FleetOperatingUnitRecord[]>([])
  const deferredSearchText = useDeferredValue(searchText)

  useEffect(() => {
    if (!organizationId) {
      setUnits([])
      return
    }

    let isActive = true
    setIsLoading(true)
    setError(null)

    void apiClient
      .getFleetOperatingUnits({ organizationId })
      .then((records) => {
        if (!isActive) {
          return
        }

        setUnits(records)
      })
      .catch((loadError) => {
        if (!isActive) {
          return
        }

        setError(
          isApiError(loadError)
            ? loadError.message
            : loadError instanceof Error
              ? loadError.message
              : "Failed to load fleet operating units."
        )
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false)
        }
      })

    return () => {
      isActive = false
    }
  }, [organizationId])

  const filteredUnits = useMemo(() => {
    const query = deferredSearchText.trim().toLowerCase()
    if (!query) {
      return units
    }

    return units.filter((unit) =>
      [
        unit.code,
        unit.name,
        unit.city,
        unit.region || "",
        unit.type,
        unit.managerName || "",
      ].some((value) => value.toLowerCase().includes(query))
    )
  }, [deferredSearchText, units])

  const summary = useMemo(() => {
    return {
      primaryCount: units.filter((unit) => unit.isPrimary).length,
      totalAssets: units.reduce((sum, unit) => sum + unit.assetCount, 0),
      totalPeople: units.reduce((sum, unit) => sum + unit.peopleCount, 0),
      uniqueCities: new Set(units.map((unit) => unit.city)).size,
    }
  }, [units])

  function resetForm() {
    setEditingUnitId(null)
    setFormState(DEFAULT_FORM_STATE)
  }

  function beginEditing(unit: FleetOperatingUnitRecord) {
    setEditingUnitId(unit.id)
    setFormState(toFormState(unit))
    setNotice(null)
    setError(null)
  }

  async function saveUnit() {
    if (!organizationId) {
      setError("No Fleet organization is selected for operating-unit management.")
      return
    }

    setIsSaving(true)
    setError(null)
    setNotice(null)

    try {
      const payload = {
        ...formState,
        organizationId,
        vertical,
      }

      const savedUnit = editingUnitId
        ? await apiClient.updateFleetOperatingUnit(editingUnitId, payload)
        : await apiClient.createFleetOperatingUnit(payload)

      setUnits((previous) => {
        const nextUnits = editingUnitId
          ? previous.map((unit) => (unit.id === savedUnit.id ? savedUnit : unit))
          : [savedUnit, ...previous]

        return savedUnit.isPrimary
          ? nextUnits.map((unit) =>
              unit.id === savedUnit.id ? unit : { ...unit, isPrimary: false }
            )
          : nextUnits
      })
      setNotice(
        editingUnitId
          ? `Updated operating unit ${savedUnit.code}.`
          : `Created operating unit ${savedUnit.code}.`
      )
      resetForm()
    } catch (saveError) {
      setError(
        isApiError(saveError)
          ? saveError.message
          : saveError instanceof Error
            ? saveError.message
            : "Failed to save the fleet operating unit."
      )
    } finally {
      setIsSaving(false)
    }
  }

  async function deleteUnit(unit: FleetOperatingUnitRecord) {
    if (!organizationId) {
      return
    }

    setIsDeletingUnitId(unit.id)
    setError(null)
    setNotice(null)

    try {
      await apiClient.deleteFleetOperatingUnit(unit.id, organizationId)
      setUnits((previous) => previous.filter((record) => record.id !== unit.id))
      if (editingUnitId === unit.id) {
        resetForm()
      }
      setNotice(`Deleted operating unit ${unit.code}.`)
    } catch (deleteError) {
      setError(
        isApiError(deleteError)
          ? deleteError.message
          : deleteError instanceof Error
            ? deleteError.message
            : "Failed to delete the fleet operating unit."
      )
    } finally {
      setIsDeletingUnitId(null)
    }
  }

  return (
    <FleetB2BPageShell
      activeSection="operating-units"
      title="Operating units"
      description="Manage branches, depots, hubs, terminals, and route bases as first-class operating units that own assets, people, escalation context, and issuance scope."
      highlights={[
        "One organization can own many operating units across cities and regions.",
        "Each asset and person is anchored to a persisted operating unit.",
        "Branch-scoped issuance, printing, and analytics now depend on this live master data.",
      ]}
    >
      <div className="grid gap-5">
        <OperatorStatusMessage>
          Active Fleet organization: <strong>{organizationLabel}</strong>.
        </OperatorStatusMessage>

        {sessionStatus === "loading" ? (
          <OperatorStatusMessage>Loading Fleet organization context…</OperatorStatusMessage>
        ) : null}
        {sessionError ? <OperatorStatusMessage tone="danger">{sessionError}</OperatorStatusMessage> : null}
        {error ? <OperatorStatusMessage tone="danger">{error}</OperatorStatusMessage> : null}
        {notice ? <OperatorStatusMessage>{notice}</OperatorStatusMessage> : null}

        {!organizationId ? (
          <OperatorPanel
            title="Organization context required"
            description="This workspace needs an organization scope from the current operator session or the latest Fleet onboarding persistence."
          >
            <OperatorEmptyState
              title="No Fleet organization is currently selected."
              message="Finish a Fleet onboarding review or open this workspace from an organization-bound operator session to manage operating units."
            />
          </OperatorPanel>
        ) : (
          <>
            <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <OperatorMetricCard
                label="Operating units"
                value={units.length}
                detail="Persisted branch, depot, hub, terminal, and route-base records."
              />
              <OperatorMetricCard
                label="Primary units"
                value={summary.primaryCount}
                detail="Only one primary unit should normally anchor the organization."
              />
              <OperatorMetricCard
                label="Asset coverage"
                value={summary.totalAssets}
                detail="Persisted assets currently linked to these units."
              />
              <OperatorMetricCard
                label="People coverage"
                value={summary.totalPeople}
                detail="Persisted people currently anchored to these units."
              />
            </section>

            <section className="grid gap-5 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
              <OperatorPanel
                title={editingUnitId ? "Edit operating unit" : "Create operating unit"}
                description="Use this form to maintain the organization’s physical and operational structure."
              >
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Unit code</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.code}
                      onChange={(event) =>
                        setFormState((previous) => ({
                          ...previous,
                          code: event.target.value.toUpperCase(),
                        }))
                      }
                      placeholder="BLR-HQ"
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Unit name</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.name}
                      onChange={(event) =>
                        setFormState((previous) => ({ ...previous, name: event.target.value }))
                      }
                      placeholder="Bengaluru HQ"
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Type</span>
                    <select
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.type}
                      onChange={(event) =>
                        setFormState((previous) => ({ ...previous, type: event.target.value }))
                      }
                    >
                      <option value="branch">Branch</option>
                      <option value="depot">Depot</option>
                      <option value="hub">Hub</option>
                      <option value="terminal">Terminal</option>
                      <option value="route_base">Route base</option>
                    </select>
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">City</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.city}
                      onChange={(event) =>
                        setFormState((previous) => ({ ...previous, city: event.target.value }))
                      }
                      placeholder="Bengaluru"
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Region / state</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.region}
                      onChange={(event) =>
                        setFormState((previous) => ({ ...previous, region: event.target.value }))
                      }
                      placeholder="Karnataka"
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Manager name</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.managerName}
                      onChange={(event) =>
                        setFormState((previous) => ({
                          ...previous,
                          managerName: event.target.value,
                        }))
                      }
                      placeholder="Branch Lead"
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Manager phone</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.managerPhone}
                      onChange={(event) =>
                        setFormState((previous) => ({
                          ...previous,
                          managerPhone: event.target.value,
                        }))
                      }
                      placeholder="+919900005555"
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Escalation phone</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.escalationPhone}
                      onChange={(event) =>
                        setFormState((previous) => ({
                          ...previous,
                          escalationPhone: event.target.value,
                        }))
                      }
                      placeholder="+919900009999"
                    />
                  </label>
                </div>

                <label className="mt-4 flex items-center gap-3 text-sm">
                  <input
                    checked={formState.isPrimary}
                    type="checkbox"
                    onChange={(event) =>
                      setFormState((previous) => ({
                        ...previous,
                        isPrimary: event.target.checked,
                      }))
                    }
                  />
                  <span>Mark this as the primary operating unit</span>
                </label>

                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => void saveUnit()}
                    disabled={isSaving}
                    className="rounded-2xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
                  >
                    {isSaving ? "Saving…" : editingUnitId ? "Save unit" : "Create unit"}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-2xl border border-border px-4 py-2 text-sm font-medium"
                  >
                    Clear form
                  </button>
                </div>
              </OperatorPanel>

              <OperatorPanel
                title="Persisted operating units"
                description="Review the units already persisted for this Fleet organization, then edit or remove them here."
              >
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  <label className="min-w-[18rem] flex-1 text-sm">
                    <span className="mb-2 block text-muted-foreground">Search units</span>
                    <input
                      className="w-full rounded-2xl border border-border bg-background px-3 py-2"
                      value={searchText}
                      onChange={(event) => setSearchText(event.target.value)}
                      placeholder="Search by code, city, manager, or type"
                    />
                  </label>
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-3 text-sm text-muted-foreground">
                    Cities covered: <strong>{summary.uniqueCities}</strong>
                  </div>
                </div>

                {isLoading ? (
                  <div className="text-sm text-muted-foreground">Loading operating units…</div>
                ) : filteredUnits.length === 0 ? (
                  <OperatorEmptyState
                    title="No operating units match the current view."
                    message="Create the first persisted unit or broaden the search to see existing records."
                  />
                ) : (
                  <div className="grid gap-3">
                    {filteredUnits.map((unit) => (
                      <div
                        key={unit.id}
                        className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-lg font-semibold">{unit.code}</span>
                              {unit.isPrimary ? (
                                <span className="rounded-full border border-primary/30 bg-primary/8 px-3 py-1 text-xs font-medium uppercase tracking-[0.16em] text-primary">
                                  Primary
                                </span>
                              ) : null}
                            </div>
                            <div className="mt-1 text-sm text-muted-foreground">
                              {unit.name} • {unit.city}
                              {unit.region ? ` • ${unit.region}` : ""}
                            </div>
                            <div className="mt-1 text-xs uppercase tracking-[0.16em] text-muted-foreground">
                              {unit.type.replace(/_/g, " ")}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => beginEditing(unit)}
                              className="rounded-2xl border border-border px-3 py-2 text-sm font-medium"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => void deleteUnit(unit)}
                              disabled={isDeletingUnitId === unit.id}
                              className="rounded-2xl border border-destructive/40 px-3 py-2 text-sm font-medium text-destructive disabled:opacity-60"
                            >
                              {isDeletingUnitId === unit.id ? "Deleting…" : "Delete"}
                            </button>
                          </div>
                        </div>

                        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                          <OperatorMetricCard
                            label="Assets"
                            value={unit.assetCount}
                            detail="Persisted assets anchored to this unit."
                          />
                          <OperatorMetricCard
                            label="People"
                            value={unit.peopleCount}
                            detail="Persisted people anchored to this unit."
                          />
                          <OperatorMetricCard
                            label="Manager"
                            value={unit.managerName || "Not set"}
                            detail={unit.managerPhone || "Manager phone not captured."}
                          />
                          <OperatorMetricCard
                            label="Updated"
                            value={formatFleetMasterTimestamp(unit.updatedAt)}
                            detail={unit.escalationPhone || "No escalation phone captured."}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </OperatorPanel>
            </section>
          </>
        )}
      </div>
    </FleetB2BPageShell>
  )
}
